import React, { useState, useEffect, useCallback } from "react";
import "../css/vitrine.css";

/* ======================================================
   CONSTANTS & DATA
====================================================== */
const API_URL = process.env.REACT_APP_API_URL || "https://kalanyoro-api.eu-4.evennode.com";

const HERO_IMG_1 = process.env.PUBLIC_URL + "/assets/images/view eleve 1.PNG";
const HERO_IMG_2 = process.env.PUBLIC_URL + "/assets/images/view eleve 2.PNG";
const HERO_IMG_3 = process.env.PUBLIC_URL + "/assets/images/view eleve 3.PNG";
const ADMIN_MOCKUP = process.env.PUBLIC_URL + "/assets/images/admin view.png";
const DEMO_VIDEO = process.env.PUBLIC_URL + "/assets/images/demo admin.mov";
const LOGO_IMG = process.env.PUBLIC_URL + "/assets/images/logo.png";

const FEATURES = {
  admin: [
    { icon: "🏫", title: "Gestion de l'école", desc: "Informations, planning, salles, classes, matières, années scolaires." },
    { icon: "📋", title: "Absences & Retards", desc: "Suivi en temps réel des présences, gestion des justificatifs par statut." },
    { icon: "📝", title: "Devoirs & Résultats", desc: "Création, suivi des devoirs et gestion des notes par période." },
    { icon: "💳", title: "Paiements & Factures", desc: "Gestion des scolarités, reçus et relevés de paiement via FedaPay & Orange Money." },
    { icon: "🔔", title: "Notifications Multi-canal", desc: "Envoi de push notifications, SMS (Orange/Twilio) et emails aux parents et élèves." },
    { icon: "👥", title: "Utilisateurs & Permissions", desc: "Gestion des comptes, rôles (admin, prof, élève) et droits d'accès granulaires." },
    { icon: "💬", title: "Groupes de Classes", desc: "Création de groupes de discussion par classe pour faciliter la communication." },
    { icon: "📊", title: "Exports & Sauvegardes", desc: "Export PDF des bulletins, relevés de paiement et sauvegarde automatique des données." },
  ],
  professeur: [
    { icon: "📅", title: "Mon Planning", desc: "Consulter son emploi du temps interactif, les salles assignées et les horaires." },
    { icon: "✍️", title: "Devoirs & Cours", desc: "Publier des devoirs, partager des supports de cours directement depuis l'app." },
    { icon: "✅", title: "Présences & Retards", desc: "Marquer les absences et retards en temps réel, directement en classe." },
    { icon: "💬", title: "Chat par Classe", desc: "Communiquer avec les élèves via des groupes de classe en temps réel (WebSocket)." },
    { icon: <img src="/assets/images/aurea-logo.png" alt="Aurea" style={{ width: '28px', height: '28px', objectFit: 'contain' }} />, title: "IA Aurea à disposition", desc: "Assistant IA pédagogique de notre partenaire Aurea pour préparer les cours." },
    { icon: "📂", title: "Fournitures Scolaires", desc: "Publier les listes de fournitures requises pour chaque matière ou classe." },
  ],
  eleve: [
    { icon: "📅", title: "Mon Planning", desc: "Visualiser son emploi du temps hebdomadaire clair et interactif." },
    { icon: "📚", title: "Cours & Devoirs", desc: "Accéder aux cours partagés et consulter les devoirs à rendre." },
    { icon: "🏆", title: "Mes Résultats", desc: "Consulter ses notes par matière et par période, avec les coefficients." },
    { icon: "💬", title: "Chat avec les Profs", desc: "Discuter avec ses professeurs dans des groupes de classe organisés." },
    { icon: <img src="/assets/images/aurea-logo.png" alt="Aurea" style={{ width: '28px', height: '28px', objectFit: 'contain' }} />, title: "IA Aurea à disposition", desc: "Assistant IA pour aider à comprendre les cours et les exercices." },
    { icon: "🔔", title: "Notifications Push", desc: "Recevoir des alertes instantanées pour les nouveaux devoirs et résultats." },
  ],
  parent: [
    { icon: "👤", title: "Compte de l'enfant", desc: "Se connecter au compte de son enfant pour tout suivre en temps réel." },
    { icon: "📱", title: "SMS d'alerte", desc: "Recevoir des SMS automatiques en cas d'absence, retard ou nouveau résultat." },
    { icon: "💰", title: "Suivi des paiements", desc: "Consulter l'état des scolarités payées, les reçus et les montants restants." },
    { icon: "🔔", title: "Notifications Importantes", desc: "Alertes push pour les informations importantes de l'établissement." },
  ],
};

const PRICING_PLANS = [
  {
    name: "Standard",
    tag: "Pour démarrer",
    emoji: "🛡️",
    desc: "Idéal pour les petites et moyennes écoles qui veulent digitaliser leur gestion scolaire.",
    price: "Sur devis",
    priceSub: "Tarif semestriel ou annuel selon effectif",
    ctaType: "outline",
    cta: "Nous contacter",
    color: "#6366f1",
    quotas: [
      { icon: "🤖", label: "IA — Élèves",   value: "100 req/mois" },
      { icon: "👨‍🏫", label: "IA — Profs",    value: "200 req/mois" },
      { icon: "💬", label: "SMS globaux",    value: "300 SMS/mois" },
      { icon: "📧", label: "Emails globaux", value: "200 emails/mois" },
    ],
    features: [
      { ok: true,  text: "Gestion complète admin, prof, élèves" },
      { ok: true,  text: "Chat en temps réel par classe" },
      { ok: true,  text: "Export PDF des bulletins et reçus" },
      { ok: true,  text: "Paiements FedaPay & Orange Money" },
      { ok: false, text: "SMS & emails augmentés" },
      { ok: false, text: "Quotas IA étendus" },
      { ok: false, text: "Support prioritaire" },
    ],
  },
  {
    name: "Premium",
    tag: "Le plus populaire",
    emoji: "⚡",
    desc: "Pour les écoles en croissance avec des besoins avancés en communication et IA.",
    price: "Sur devis",
    priceSub: "Tarif selon effectif et options choisies",
    featured: true,
    ctaType: "primary",
    cta: "Démarrer maintenant",
    color: "#8b5cf6",
    quotas: [
      { icon: "🤖", label: "IA — Élèves",   value: "500 req/mois" },
      { icon: "👨‍🏫", label: "IA — Profs",    value: "1 000 req/mois" },
      { icon: "💬", label: "SMS globaux",    value: "1 500 SMS/mois" },
      { icon: "📧", label: "Emails globaux", value: "1 000 emails/mois" },
    ],
    features: [
      { ok: true, text: "Tout ce qui est dans Standard" },
      { ok: true, text: "Quotas IA ×5 pour élèves & profs" },
      { ok: true, text: "SMS & emails ×5" },
      { ok: true, text: "Sauvegardes automatiques quotidiennes" },
      { ok: true, text: "Exports comptables avancés" },
      { ok: true, text: "Support prioritaire" },
      { ok: true, text: "Rapports mensuels aux parents" },
    ],
  },
  {
    name: "VIP",
    tag: "Accès illimité",
    emoji: "👑",
    desc: "Pour les grands réseaux d'écoles. Quotas illimités, branding personnalisé et accompagnement dédié.",
    price: "Contactez-nous",
    priceSub: "Devis sur mesure selon vos besoins",
    ctaType: "gold",
    cta: "Discutons ensemble",
    color: "#f59e0b",
    quotas: [
      { icon: "🤖", label: "IA — Élèves",   value: "Illimité ∞" },
      { icon: "👨‍🏫", label: "IA — Profs",    value: "Illimité ∞" },
      { icon: "💬", label: "SMS globaux",    value: "Illimité ∞" },
      { icon: "📧", label: "Emails globaux", value: "Illimité ∞" },
    ],
    features: [
      { ok: true, text: "Tout ce qui est dans Premium" },
      { ok: true, text: "Quotas IA & SMS illimités" },
      { ok: true, text: "Branding personnalisé (votre logo)" },
      { ok: true, text: "Multi-établissements" },
      { ok: true, text: "Formation de votre équipe" },
      { ok: true, text: "SLA et support dédié" },
      { ok: true, text: "Intégrations sur mesure" },
    ],
  },
];

const NAV_LINKS = [
  { href: "#fonctionnalites", label: "Fonctionnalités" },
  { href: "#demo", label: "Démo" },
  { href: "#tarifs", label: "Tarifs" },
  { href: "#contact", label: "Contact" },
];

/* ======================================================
   HOOKS
====================================================== */
function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".reveal").forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

function useNavbarScroll() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return scrolled;
}

/* ======================================================
   RATE LIMIT HELPER (localStorage)
====================================================== */
const RATE_KEY = "ky_contact_ts";
const MAX_SENDS = 5; // Temporairement augmenté pour les tests (au lieu de 3)
const WINDOW_MS = 60 * 60 * 1000; // 1h

function checkClientRateLimit() {
  try {
    const raw = localStorage.getItem(RATE_KEY);
    const now = Date.now();
    const timestamps = raw ? JSON.parse(raw).filter(t => now - t < WINDOW_MS) : [];
    if (timestamps.length >= MAX_SENDS) {
      const oldest = Math.min(...timestamps);
      const remaining = Math.ceil((WINDOW_MS - (now - oldest)) / 60000);
      return { allowed: false, remaining };
    }
    timestamps.push(now);
    localStorage.setItem(RATE_KEY, JSON.stringify(timestamps));
    return { allowed: true };
  } catch {
    return { allowed: true };
  }
}

/* ======================================================
   VALIDATION
====================================================== */
function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}
function validatePhone(phone) {
  if (!phone) return true;
  return /^([+]|00)?[0-9\s-]{8,18}$/.test(phone.trim());
}
function sanitize(str) {
  return str.replace(/[<>]/g, "").trim().slice(0, 2000);
}

/* ======================================================
   COMPONENTS
====================================================== */

/* --- Navbar --- */
function Navbar({ menuOpen, setMenuOpen }) {
  const scrolled = useNavbarScroll();
  const handleNav = (e, href) => {
    e.preventDefault();
    setMenuOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <>
      <nav className={`ky-navbar${scrolled ? " scrolled" : ""}`} role="navigation" aria-label="Navigation principale">
        <div className="navbar-inner">
          <a className="ky-logo" href="#hero" onClick={e => handleNav(e, "#hero")} style={{ display: "flex", alignItems: "center" }}>
            <img src={LOGO_IMG} alt="KalanYoro Logo" style={{ height: "45px", width: "auto" }} />
          </a>
          <ul className="nav-links">
            {NAV_LINKS.map(l => (
              <li key={l.href}>
                <a href={l.href} onClick={e => handleNav(e, l.href)}>{l.label}</a>
              </li>
            ))}
          </ul>
          <div className="nav-cta">
            <a href="https://kalanyoro-admin.savantech.org" target="_blank" rel="noopener noreferrer"
               className="btn-ky btn-outline" style={{ padding: "10px 20px", fontSize: "0.9rem" }}>
              Espace Admin
            </a>
            <a href="#contact" onClick={e => handleNav(e, "#contact")}
               className="btn-ky btn-primary" style={{ padding: "10px 20px", fontSize: "0.9rem" }}>
              Nous rejoindre
            </a>
          </div>
          <button className="hamburger" aria-label="Menu" aria-expanded={menuOpen}
                  onClick={() => setMenuOpen(o => !o)}>
            <span /><span /><span />
          </button>
        </div>
      </nav>
      <div className={`mobile-menu${menuOpen ? " open" : ""}`} role="dialog" aria-label="Menu mobile">
        {NAV_LINKS.map(l => (
          <a key={l.href} href={l.href} onClick={e => handleNav(e, l.href)}>{l.label}</a>
        ))}
        <a href="https://kalanyoro-admin.savantech.org" target="_blank" rel="noopener noreferrer"
           style={{ color: "var(--ky-green-light)" }}>Espace Admin ↗</a>
        <a href="#contact" onClick={e => handleNav(e, "#contact")}
           style={{ color: "var(--ky-green-light)" }}>Nous rejoindre →</a>
      </div>
    </>
  );
}

/* --- Hero --- */
function Hero() {
  return (
    <section id="hero" className="hero-section">
      <div className="hero-bg" aria-hidden="true" />
      <div className="hero-grid" aria-hidden="true" />
      <div className="container-ky">
        <div className="hero-content">
          <div className="hero-left reveal">
            <div className="hero-badge">
              <span className="dot" aria-hidden="true" />
              Nouveau • Technologie Africaine
            </div>
            <h2 className="hero-title">
              La plateforme scolaire{" "}
              <span className="gradient-text">tout-en-un</span>{" "}
            </h2>
            <p className="hero-desc">
              KalanYoro connecte administrateurs, professeurs, élèves et parents dans une seule application.
              Planning, absences, résultats, paiements, chat et IA — tout y est.
            </p>
            <div className="hero-actions">
              <a href="#contact" className="btn-ky btn-primary"
                 onClick={e => { e.preventDefault(); document.querySelector("#contact").scrollIntoView({ behavior: "smooth" }); }}>
                🚀 Démarrer gratuitement
              </a>
              <a href="#demo" className="btn-ky btn-outline"
                 onClick={e => { e.preventDefault(); document.querySelector("#demo").scrollIntoView({ behavior: "smooth" }); }}>
                ▶ Voir la démo
              </a>
            </div>
            <div className="store-buttons">
              <a href="https://play.google.com/store/apps/details?id=com.wendyam23.kalanyoro"
                 target="_blank" rel="noopener noreferrer"
                 className="store-btn" aria-label="Télécharger KalanYoro sur Google Play">
                <span className="store-icon" aria-hidden="true">🤖</span>
                <span className="store-text">
                  <span className="store-sub">Disponible sur</span>
                  <span className="store-name">Google Play</span>
                </span>
              </a>
              <a href="https://apps.apple.com/us/app/kalanyoro/id6762062773"
                 target="_blank" rel="noopener noreferrer"
                 className="store-btn" aria-label="Télécharger KalanYoro sur l'App Store">
                <span className="store-icon" aria-hidden="true">🍎</span>
                <span className="store-text">
                  <span className="store-sub">Disponible sur</span>
                  <span className="store-name">App Store</span>
                </span>
              </a>
              <a href="https://kalanyoro-admin.savantech.org" target="_blank" rel="noopener noreferrer"
                 className="store-btn" aria-label="Accéder à l'interface Admin">
                <span className="store-icon" aria-hidden="true">🖥️</span>
                <span className="store-text">
                  <span className="store-sub">Accéder à</span>
                  <span className="store-name">Interface Admin</span>
                </span>
              </a>
            </div>

            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-value gradient-text">4</span>
                <span className="stat-label">Profils utilisateurs</span>
              </div>
              <div className="stat-item">
                <span className="stat-value gradient-text">1000+</span>
                <span className="stat-label">Élèves supportés</span>
              </div>
              <div className="stat-item">
                <span className="stat-value gradient-text">100%</span>
                <span className="stat-label">Digital</span>
              </div>
            </div>
          </div>
          <div className="hero-visual reveal reveal-delay-2">
            <div className="hero-image-wrapper" style={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
              <img src={HERO_IMG_2} alt="KalanYoro View 2" style={{ width: "35%", position: "absolute", left: "5%", zIndex: 1, borderRadius: "20px", opacity: 0.85, transform: "scale(0.9) rotate(-5deg)", boxShadow: "0 10px 30px rgba(0,0,0,0.5)" }} />
              <img src={HERO_IMG_3} alt="KalanYoro View 3" style={{ width: "35%", position: "absolute", right: "5%", zIndex: 1, borderRadius: "20px", opacity: 0.85, transform: "scale(0.9) rotate(5deg)", boxShadow: "0 10px 30px rgba(0,0,0,0.5)" }} />
              <img src={HERO_IMG_1} alt="KalanYoro View 1" style={{ width: "45%", zIndex: 2, borderRadius: "20px", boxShadow: "0 20px 40px rgba(0,0,0,0.7)" }} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --- Features --- */
function Features() {
  const [activeTab, setActiveTab] = useState("admin");
  const tabs = [
    { id: "admin",      label: "🏫 Admin" },
    { id: "professeur", label: "👨‍🏫 Professeurs" },
    { id: "eleve",      label: "🎓 Élèves" },
    { id: "parent",     label: "👨‍👩‍👧 Parents" },
  ];

  return (
    <section id="fonctionnalites" className="section features-section">
      <div className="container-ky">
        <div className="reveal">
          <span className="section-label">✨ Fonctionnalités</span>
          <h2 className="section-title">Tout ce dont votre école a besoin</h2>
          <p className="section-subtitle">
            Une plateforme adaptée à chaque acteur de l'école — de l'administrateur aux parents.
          </p>
        </div>

        <div className="role-tabs reveal reveal-delay-1" role="tablist" aria-label="Sélectionner un profil">
          {tabs.map(t => (
            <button key={t.id}
                    role="tab" aria-selected={activeTab === t.id}
                    className={`role-tab${activeTab === t.id ? " active" : ""}`}
                    onClick={() => setActiveTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="features-grid" role="tabpanel" key={activeTab}
             style={{ animation: "fadeInUp 0.4s ease" }}>
          {FEATURES[activeTab].map((f) => (
            <div key={f.title} className="glass-card feature-card">
              <div className="feature-icon" aria-hidden="true">{f.icon}</div>
              <h3 className="feature-title">{f.title}</h3>
              <p className="feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="reveal" style={{ marginTop: "60px" }}>
          <div className="glass-card" style={{ padding: "32px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "40px", alignItems: "center" }}>
            <div>
              <h3 style={{ color: "white", marginBottom: "12px" }}>Interface d'administration web</h3>
              <p style={{ marginBottom: "20px" }}>
                Gérez votre établissement depuis n'importe quel navigateur. Tableau de bord intuitif,
                rapports détaillés, et gestion complète de vos classes et paiements.
              </p>
              <a href="https://kalanyoro-admin.savantech.org" target="_blank" rel="noopener noreferrer"
                 className="btn-ky btn-primary">
                Accéder à l'Admin ↗
              </a>
            </div>
            <div style={{ borderRadius: "var(--ky-radius)", overflow: "hidden", border: "1px solid var(--ky-border)" }}>
              <img src={ADMIN_MOCKUP} alt="Interface d'administration KalanYoro"
                   style={{ width: "100%", height: "auto", display: "block" }} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --- Video Section --- */
function VideoSection() {
  const [playing, setPlaying] = useState(false);
  return (
    <section id="demo" className="section video-section">
      <div className="container-ky" style={{ textAlign: "center" }}>
        <div className="reveal">
          <span className="section-label">🎬 Démo</span>
          <h2 className="section-title">Voyez KalanYoro en action</h2>
          <p className="section-subtitle" style={{ margin: "0 auto 48px" }}>
            Découvrez comment notre application transforme la gestion scolaire au quotidien.
          </p>
        </div>
        <div className="video-wrapper reveal reveal-delay-1" onClick={() => setPlaying(true)}>
          {!playing ? (
            <div className="video-placeholder">
              <div className="video-decoration" aria-hidden="true" />
              <button className="play-btn" aria-label="Lancer la vidéo de démonstration">
                ▶
              </button>
              <p className="vp-label">Aperçu vidéo</p>
              <p style={{ fontSize: "0.85rem", color: "var(--ky-text-muted)" }}>
                Cliquez pour voir KalanYoro en action
              </p>
            </div>
          ) : (
            <video 
              src={DEMO_VIDEO} 
              autoPlay 
              controls 
              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "var(--ky-radius)", outline: "none", background: "black" }}
            />
          )}
        </div>
        <div style={{ marginTop: "32px" }}>
          <a href="https://wa.me/22604894780?text=Bonjour%2C%20je%20voudrais%20une%20démo%20de%20KalanYoro"
             target="_blank" rel="noopener noreferrer"
             className="btn-ky btn-outline">
            💬 Demander une démo personnalisée sur WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
}

/* --- Pricing --- */
function Pricing({ onContact }) {
  const [hoveredPlan, setHoveredPlan] = React.useState(null);
  return (
    <section id="tarifs" className="section pricing-section">
      <div className="container-ky">
        <div className="reveal">
          <span className="section-label">💰 Tarifs</span>
          <h2 className="section-title">Des formules adaptées à votre école</h2>
          <p className="section-subtitle">
            Nous proposons des tarifs flexibles pour les établissements du Burkina Faso et du Sénégal.
            Tous les prix sont négociés directement avec l'école selon l'effectif et les besoins.
          </p>
        </div>

        {/* Plan Cards */}
        <div className="pricing-cards">
          {PRICING_PLANS.map((plan, i) => (
            <div
              key={plan.name}
              className={`pricing-card reveal reveal-delay-${i + 1}${plan.featured ? " featured" : ""}`}
              onMouseEnter={() => setHoveredPlan(plan.name)}
              onMouseLeave={() => setHoveredPlan(null)}
              style={hoveredPlan === plan.name ? { transform: "translateY(-6px)", transition: "transform 0.25s ease" } : { transition: "transform 0.25s ease" }}
            >
              {plan.featured && <div className="pricing-badge">⭐ Recommandé</div>}

              {/* Plan header */}
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                <span style={{ fontSize: "1.6rem" }}>{plan.emoji}</span>
                <div className="plan-name">{plan.name}</div>
              </div>
              <div style={{
                display: "inline-block",
                fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.05em",
                textTransform: "uppercase", padding: "3px 10px",
                borderRadius: "999px", marginBottom: "12px",
                background: plan.featured ? "rgba(139,92,246,0.15)" : "rgba(255,255,255,0.06)",
                color: plan.color,
                border: `1px solid ${plan.color}33`,
              }}>{plan.tag}</div>
              <div className="plan-desc">{plan.desc}</div>

              {/* Price */}
              <div className="plan-price">
                <div className="price-main gradient-text">{plan.price}</div>
                <div className="price-sub">{plan.priceSub}</div>
              </div>

              {/* Quota badges */}
              <div style={{
                background: "rgba(255,255,255,0.04)",
                borderRadius: "12px",
                padding: "14px",
                marginBottom: "20px",
                border: "1px solid rgba(255,255,255,0.07)",
              }}>
                <p style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(255,255,255,0.4)", marginBottom: "10px", fontWeight: 600 }}>
                  📊 Inclus chaque mois
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                  {plan.quotas.map(q => (
                    <div key={q.label} style={{
                      display: "flex", flexDirection: "column", gap: "2px",
                      background: "rgba(255,255,255,0.03)",
                      borderRadius: "8px", padding: "8px 10px",
                      border: `1px solid ${plan.color}22`,
                    }}>
                      <span style={{ fontSize: "1rem" }}>{q.icon}</span>
                      <span style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.45)", fontWeight: 500 }}>{q.label}</span>
                      <span style={{
                        fontSize: q.value.includes("∞") ? "1.1rem" : "0.8rem",
                        fontWeight: 700,
                        color: q.value.includes("∞") ? "#fbbf24" : "white",
                      }}>{q.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Features */}
              <ul className="plan-features" style={{ marginBottom: "24px" }}>
                {plan.features.map(f => (
                  <li key={f.text}>
                    <span className={f.ok ? "check" : "cross"} aria-hidden="true">
                      {f.ok ? "✓" : "✗"}
                    </span>
                    <span style={{ color: f.ok ? "var(--ky-text-muted)" : "#6b7280" }}>{f.text}</span>
                  </li>
                ))}
              </ul>

              <button
                className={`btn-ky btn-${plan.ctaType}`}
                style={{ width: "100%", justifyContent: "center" }}
                onClick={() => onContact(plan.name)}>
                {plan.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Comparison table */}
        <div className="reveal" style={{ marginTop: "56px" }}>
          <p style={{ textAlign: "center", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(255,255,255,0.4)", fontWeight: 600, marginBottom: "20px" }}>
            📋 Comparaison des quotas mensuels
          </p>
          <div style={{ overflowX: "auto" }}>
            <table style={{
              width: "100%", borderCollapse: "collapse",
              background: "rgba(255,255,255,0.03)",
              borderRadius: "16px", overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.07)",
              fontSize: "0.88rem",
            }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.05)" }}>
                  <th style={{ padding: "14px 20px", textAlign: "left", color: "rgba(255,255,255,0.5)", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>Ressource</th>
                  {PRICING_PLANS.map(p => (
                    <th key={p.name} style={{ padding: "14px 20px", textAlign: "center", color: p.color, fontWeight: 700, fontSize: "0.85rem" }}>
                      {p.emoji} {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { icon: "🤖", label: "IA — Élèves",    values: ["100", "500", "∞"] },
                  { icon: "👨‍🏫", label: "IA — Profs",     values: ["200", "1 000", "∞"] },
                  { icon: "💬", label: "SMS / mois",      values: ["300", "1 500", "∞"] },
                  { icon: "📧", label: "Emails / mois",   values: ["200", "1 000", "∞"] },
                  { icon: "📊", label: "Bulletins auto",  values: ["✓", "✓", "✓"] },
                  { icon: "📱", label: "Rapports parents",values: ["✓", "✓", "✓"] },
                  { icon: "🎯", label: "Support",         values: ["Standard", "Prioritaire", "Dédié"] },
                ].map((row, ri) => (
                  <tr key={row.label} style={{ borderTop: "1px solid rgba(255,255,255,0.05)", background: ri % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)" }}>
                    <td style={{ padding: "12px 20px", color: "rgba(255,255,255,0.7)", display: "flex", gap: "8px", alignItems: "center" }}>
                      <span>{row.icon}</span> {row.label}
                    </td>
                    {row.values.map((val, vi) => (
                      <td key={vi} style={{
                        padding: "12px 20px", textAlign: "center",
                        fontWeight: val === "∞" ? 800 : 600,
                        color: val === "∞" ? "#fbbf24" : val === "✓" ? "#4ade80" : "white",
                        fontSize: val === "∞" ? "1.15rem" : "0.88rem",
                      }}>{val}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Note */}
        <div className="reveal" style={{ marginTop: "32px", textAlign: "center" }}>
          <div className="glass-card" style={{ padding: "24px 32px", display: "inline-block", maxWidth: "620px" }}>
            <p style={{ fontSize: "0.9rem", marginBottom: "8px" }}>
              💡 <strong style={{ color: "white" }}>Des crédits supplémentaires disponibles</strong>
            </p>
            <p style={{ fontSize: "0.88rem" }}>
              Vous avez dépassé vos quotas ? Votre administrateur peut demander des packs de crédits
              supplémentaires (SMS, emails, requêtes IA) à tout moment, sans changer de formule.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --- AI Partner --- */
function AIPartner() {
  return (
    <section className="section ai-section">
      <div className="container-ky">
        <div className="ai-card reveal">
          <div className="ai-info">
            <div className="ai-badge">🤖 Intelligence Artificielle</div>
            <h2 style={{ color: "white", marginBottom: "16px" }}>
              L'IA <span className="gradient-text-gold">Aurea</span> intégrée
            </h2>
            <p style={{ marginBottom: "24px", fontSize: "1.05rem" }}>
              KalanYoro intègre nativement l'IA de notre partenaire <strong style={{ color: "white" }}>Aurea</strong>,
              une solution d'intelligence artificielle conçue pour l'Afrique.
            </p>
            <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "12px", marginBottom: "32px" }} aria-label="Fonctionnalités Aurea">
              {[
                "Aide les professeurs à préparer leurs cours",
                "Assiste les élèves dans la compréhension des leçons",
                "Répond aux questions pédagogiques en temps réel",
                "Disponible 24h/24, 7j/7 dans l'application",
              ].map(t => (
                <li key={t} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                  <span style={{ color: "var(--ky-gold)", fontSize: "1rem" }}>✦</span>
                  <span style={{ color: "var(--ky-text-muted)", fontSize: "0.95rem" }}>{t}</span>
                </li>
              ))}
            </ul>
            <a href="https://aurea-bf.com" target="_blank" rel="noopener noreferrer"
               className="btn-ky btn-gold">
              Découvrir Aurea ↗
            </a>
          </div>
          <div className="ai-visual">
            <div className="ai-bubble-container" aria-hidden="true">
              <div className="ai-ring ai-ring-1" />
              <div className="ai-ring ai-ring-2" />
              <div className="ai-circle">
                <img src="/assets/images/aurea-logo.png" alt="Aurea Logo" style={{ width: '70%', height: '70%', objectFit: 'contain' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --- Contact Form --- */
function ContactForm({ initialType }) {
  const [form, setForm] = useState({
    nom: "", email: "", telephone: "",
    sujet: "", message: "", type_demande: initialType || "contact",
    honeypot: "",
  });
  const [errors, setErrors] = useState({});
  const [humanCheck, setHumanCheck] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [rateLimitMsg, setRateLimitMsg] = useState(null);
  const [serverError, setServerError] = useState(null);

  useEffect(() => {
    if (initialType) setForm(f => ({ ...f, type_demande: initialType }));
  }, [initialType]);

  const validate = useCallback(() => {
    const e = {};
    if (!form.nom.trim() || form.nom.trim().length < 2) e.nom = "Nom requis (min. 2 caractères)";
    if (!form.email.trim() || !validateEmail(form.email)) e.email = "Adresse email invalide";
    if (form.telephone && !validatePhone(form.telephone)) e.telephone = "Numéro de téléphone invalide";
    if (!form.sujet.trim() || form.sujet.trim().length < 2) e.sujet = "Sujet requis";
    if (!form.message.trim() || form.message.trim().length < 10) e.message = "Message trop court (min. 10 caractères)";
    if (!humanCheck) e.humanCheck = "Veuillez confirmer que vous n'êtes pas un robot";
    return e;
  }, [form, humanCheck]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setServerError(null);
    setSuccess(false);

    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    const rl = checkClientRateLimit();
    if (!rl.allowed) {
      setRateLimitMsg(`Trop d'envois. Réessayez dans ${rl.remaining} minute(s).`);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        nom: sanitize(form.nom),
        email: form.email.trim(),
        telephone: form.telephone.trim(),
        sujet: sanitize(form.sujet),
        message: sanitize(form.message),
        type_demande: form.type_demande,
        honeypot: form.honeypot,
      };

      const res = await fetch(`${API_URL}/vitrine/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.status === 429) {
        setRateLimitMsg("Trop de requêtes. Veuillez patienter avant de renvoyer.");
      } else if (data.state === "success") {
        setSuccess(true);
        setForm({ nom: "", email: "", telephone: "", sujet: "", message: "", type_demande: "contact", honeypot: "" });
        setHumanCheck(false);
        setRateLimitMsg(null);
      } else {
        setServerError(data.message || "Une erreur est survenue.");
      }
    } catch {
      setServerError("Impossible de contacter le serveur. Écrivez-nous directement sur WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate aria-label="Formulaire de contact KalanYoro">
      {/* Honeypot — caché des humains */}
      <div className="ky-honeypot" aria-hidden="true">
        <label>Ne pas remplir ce champ
          <input type="text" name="honeypot" value={form.honeypot}
                 onChange={handleChange} tabIndex="-1" autoComplete="off" />
        </label>
      </div>

      {success && (
        <div className="form-success" role="alert">
          ✅ Message envoyé avec succès ! Nous vous répondrons dans les 24–48h.
        </div>
      )}
      {serverError && (
        <div className="form-error" role="alert" style={{ marginBottom: "16px", fontSize: "0.9rem" }}>
          ⚠️ {serverError}
        </div>
      )}
      {rateLimitMsg && <div className="rate-limit-msg" role="alert">⏳ {rateLimitMsg}</div>}

      <div className="form-group">
        <label className="form-label" htmlFor="type_demande">Type de demande</label>
        <select id="type_demande" name="type_demande" className="form-select"
                value={form.type_demande} onChange={handleChange}>
          <option value="contact">📬 Prise de contact générale</option>
          <option value="rejoindre">🤝 Rejoindre KalanYoro (mon école)</option>
          <option value="devis">💼 Demande de devis</option>
        </select>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label" htmlFor="nom">Nom complet *</label>
          <input id="nom" name="nom" type="text" className="form-input"
                 placeholder="Votre nom" value={form.nom} onChange={handleChange}
                 autoComplete="name" maxLength={100} />
          {errors.nom && <div className="form-error" role="alert">{errors.nom}</div>}
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="email">Email *</label>
          <input id="email" name="email" type="email" className="form-input"
                 placeholder="votre@email.com" value={form.email} onChange={handleChange}
                 autoComplete="email" maxLength={200} />
          {errors.email && <div className="form-error" role="alert">{errors.email}</div>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label" htmlFor="telephone">Téléphone / WhatsApp</label>
          <input id="telephone" name="telephone" type="tel" className="form-input"
                 placeholder="+226 XX XX XX XX" value={form.telephone} onChange={handleChange}
                 autoComplete="tel" maxLength={20} />
          {errors.telephone && <div className="form-error" role="alert">{errors.telephone}</div>}
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="sujet">Sujet *</label>
          <input id="sujet" name="sujet" type="text" className="form-input"
                 placeholder="En quelques mots..." value={form.sujet} onChange={handleChange}
                 maxLength={200} />
          {errors.sujet && <div className="form-error" role="alert">{errors.sujet}</div>}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="message">Votre message *</label>
        <textarea id="message" name="message" className="form-textarea"
                  placeholder="Décrivez votre école, vos besoins, vos questions..."
                  value={form.message} onChange={handleChange} maxLength={2000} rows={5} />
        <div style={{ fontSize: "0.75rem", color: "var(--ky-text-muted)", textAlign: "right" }}>
          {form.message.length}/2000
        </div>
        {errors.message && <div className="form-error" role="alert">{errors.message}</div>}
      </div>

      {/* Human check */}
      <div className="human-check">
        <input type="checkbox" id="humanCheck" checked={humanCheck}
               onChange={(e) => setHumanCheck(e.target.checked)}
               aria-required="true" />
        <label htmlFor="humanCheck" className="human-check-label">
          ☑ Je confirme que je suis un humain et non un robot automatisé.
        </label>
      </div>
      {errors.humanCheck && <div className="form-error" role="alert">{errors.humanCheck}</div>}

      <button type="submit" className="btn-ky btn-primary"
              style={{ width: "100%", justifyContent: "center", fontSize: "1rem", padding: "16px" }}
              disabled={loading || !!rateLimitMsg}>
        {loading ? "⏳ Envoi en cours..." : "📤 Envoyer le message"}
      </button>
    </form>
  );
}

/* --- Contact Section --- */
function Contact({ contactType, setContactType }) {
  return (
    <section id="contact" className="section contact-section">
      <div className="container-ky">
        <div className="reveal" style={{ textAlign: "center" }}>
          <span className="section-label">📬 Contact & Rejoindre</span>
          <h2 className="section-title">Parlons de votre école</h2>
          <p className="section-subtitle" style={{ margin: "0 auto 60px" }}>
            Prêt à digitaliser votre établissement ? Contactez-nous pour un devis ou une démo personnalisée.
          </p>
        </div>
        <div className="contact-grid">
          <div className="contact-info reveal">
            <div className="contact-item">
              <div className="contact-item-icon" aria-hidden="true">💬</div>
              <div className="contact-item-text">
                <h4>WhatsApp</h4>
                <a href="https://wa.me/22604894780" target="_blank" rel="noopener noreferrer">
                  +226 04 89 47 80
                </a>
                <p>Réponse rapide, 7j/7</p>
              </div>
            </div>
            <div className="contact-item">
              <div className="contact-item-icon" aria-hidden="true">📧</div>
              <div className="contact-item-text">
                <h4>Email</h4>
                <a href="mailto:savantech25@gmail.com">savantech25@gmail.com</a>
                <p>Réponse sous 24–48h ouvrables</p>
              </div>
            </div>
            <div className="contact-item">
              <div className="contact-item-icon" aria-hidden="true">🌍</div>
              <div className="contact-item-text">
                <h4>Zones desservies</h4>
                <p>🇧🇫 Burkina Faso — 🇸🇳 Sénégal</p>
                <p>Expansion en cours dans d'autres pays</p>
              </div>
            </div>
            <div className="contact-item">
              <div className="contact-item-icon" aria-hidden="true">🏢</div>
              <div className="contact-item-text">
                <h4>SavanTech</h4>
                <a href="https://savantech.org" target="_blank" rel="noopener noreferrer">
                  savantech.org ↗
                </a>
                <p>Solutions tech pour l'Afrique</p>
              </div>
            </div>
          </div>
          <div className="contact-form-wrapper reveal reveal-delay-2">
            <h3 style={{ color: "white", marginBottom: "24px" }}>Envoyez-nous un message</h3>
            <ContactForm initialType={contactType} />
          </div>
        </div>
      </div>
    </section>
  );
}

/* --- SavanTech Strip --- */
function CompanyStrip() {
  return (
    <div className="company-strip">
      <div className="company-strip-inner">
        <span>🏢 Développé par</span>
        <a href="https://savantech.org" target="_blank" rel="noopener noreferrer">SavanTech</a>
        <span>—</span>
        <span>Solutions technologiques pour l'Afrique</span>
        <span>·</span>
        <a href="https://aurea-bf.com" target="_blank" rel="noopener noreferrer">IA Aurea</a>
        <span>·</span>
        <a href="https://kalanyoro-admin.savantech.org" target="_blank" rel="noopener noreferrer">Admin Web ↗</a>
      </div>
    </div>
  );
}

/* --- Footer --- */
function Footer() {
  const handleNav = (e, href) => {
    e.preventDefault();
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <footer className="ky-footer">
      <div className="container-ky">
        <div className="footer-grid">
          <div className="footer-brand">
            <div className="ky-logo" style={{ marginBottom: "16px", textDecoration: "none", display: "flex", alignItems: "center" }}>
              <img src={LOGO_IMG} alt="KalanYoro Logo" style={{ height: "55px", width: "auto" }} />
            </div>
            <p>
              La plateforme scolaire tout-en-un. Connectons administrateurs,
              enseignants, élèves et parents dans une seule app.
            </p>
            <div style={{ display: "flex", gap: "12px", marginTop: "20px", flexWrap: "wrap" }}>
              <a href="https://wa.me/22604894780" target="_blank" rel="noopener noreferrer"
                 className="btn-ky btn-outline" style={{ padding: "8px 16px", fontSize: "0.85rem" }}>
                💬 WhatsApp
              </a>
              <a href="mailto:savantech25@gmail.com"
                 className="btn-ky btn-outline" style={{ padding: "8px 16px", fontSize: "0.85rem" }}>
                📧 Email
              </a>
            </div>
          </div>
          <div className="footer-links">
            <h4>Navigation</h4>
            <ul>
              <li><a href="#fonctionnalites" onClick={e => handleNav(e, "#fonctionnalites")}>Fonctionnalités</a></li>
              <li><a href="#demo" onClick={e => handleNav(e, "#demo")}>Démo</a></li>
              <li><a href="#tarifs" onClick={e => handleNav(e, "#tarifs")}>Tarifs</a></li>
              <li><a href="#contact" onClick={e => handleNav(e, "#contact")}>Contact</a></li>
            </ul>
          </div>
          <div className="footer-links">
            <h4>Accès</h4>
            <ul>
              <li><a href="https://kalanyoro-admin.savantech.org" target="_blank" rel="noopener noreferrer">Admin Web</a></li>
              <li><a href="https://play.google.com/store/apps/details?id=com.wendyam23.kalanyoro" target="_blank" rel="noopener noreferrer">App Android</a></li>
              <li><a href="https://apps.apple.com/us/app/kalanyoro/id6762062773" target="_blank" rel="noopener noreferrer">App iOS</a></li>
              <li><a href="https://aurea-bf.com" target="_blank" rel="noopener noreferrer">IA Aurea</a></li>
            </ul>
          </div>

          <div className="footer-links">
            <h4>SavanTech</h4>
            <ul>
              <li><a href="https://savantech.org" target="_blank" rel="noopener noreferrer">Site officiel</a></li>
              <li><a href="mailto:savantech25@gmail.com">Contact</a></li>
              <li><a href="https://wa.me/22604894780" target="_blank" rel="noopener noreferrer">WhatsApp</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} KalanYoro by <a href="https://savantech.org" target="_blank" rel="noopener noreferrer">SavanTech</a>. Tous droits réservés.</p>
          <p style={{ fontSize: "0.8rem" }}>🌍 Made with ❤️ for Africa — 🇧🇫 🇸🇳</p>
        </div>
      </div>
    </footer>
  );
}

/* ======================================================
   MAIN APP
====================================================== */
export default function Home() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactType, setContactType] = useState("contact");

  useScrollReveal();

  const handlePricingContact = useCallback((planName) => {
    const map = { Basic: "devis", Premium: "devis", Personnalisé: "devis" };
    setContactType(map[planName] || "contact");
    setTimeout(() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }), 50);
  }, []);

  return (
    <div className="ky-app">
      <Navbar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <main id="main-content">
        <Hero />
        <div className="section-divider" />
        <Features />
        <div className="section-divider" />
        <VideoSection />
        <div className="section-divider" />
        <Pricing onContact={handlePricingContact} />
        <div className="section-divider" />
        <AIPartner />
        <div className="section-divider" />
        <Contact contactType={contactType} setContactType={setContactType} />
      </main>
      <CompanyStrip />
      <Footer />
    </div>
  );
}
